import { useEffect, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { useNotify } from './useNotify'
import { queryToObjectConverter } from '../helpers/apiHelper'

export default function useInfiniteScroll(
  requestFunction,
  updateAction,
  apiHelper = null,
  query = '',
  clearFunction = null,
  withoutDispatch = false
) {
  const [loading, setLoading] = useState(true)
  const [nextPage, setNextPage] = useState(1)
  const [dataCount, setDataCount] = useState(0)
  const [isListEmpty, setIsListEmpty] = useState(false)
  const [fetchStop, setFetchStop] = useState(false)
  const [fetchPage, setFetchPage] = useState(1)

  const currentPage = useRef()
  const isFetching = useRef(false)
  const dispatch = useDispatch()
  const { notify } = useNotify()

  const queryRef = useRef({ current: null, previous: null })

  currentPage.current = nextPage

  const defaultGetFunc = (clearFunc) => {
    setLoading(true) // Устанавливаем loading в true перед началом загрузки

    const pageToFetch = currentPage.current || 1
    requestFunction(pageToFetch, query)
      .then((data) => {
        if (!data.ok && data.errors) {
          data.errors.forEach(({ message }) => notify.error('Error', message))
        } else {
          if (clearFunc) dispatch(clearFunc())
          if (!withoutDispatch && apiHelper) {
            dispatch(updateAction(apiHelper(data.results, data.count, data)))
          } else if (withoutDispatch) {
            updateAction((prev) => [...prev, ...data.results])
          }
          setDataCount(data.count)
          if (data.next === null) setNextPage(null)
        }
      })
      .catch(({ errors }) => {
        notify.errorsList(errors)
      })
      .finally(() => {
        isFetching.current = false
        setLoading(false) // Устанавливаем loading в false только после завершения всех операций
      })
  }

  const fetchWithClearRedux = () => {
    setFetchStop(true)
    setFetchPage(null)
    currentPage.current = 1
    defaultGetFunc(clearFunction)
  }

  useEffect(() => {
    if (withoutDispatch) updateAction([])

    setNextPage(1)
    setFetchPage(1)
    currentPage.current = 1

    if (isListEmpty) setIsListEmpty(false)

    if (clearFunction) dispatch(clearFunction())

    // eslint-disable-next-line
  }, [query, clearFunction, isListEmpty])

  useEffect(() => {
    if (isFetching.current) return

    queryRef.current.previous = queryRef.current.current
    queryRef.current.current = query

    if (!Boolean(nextPage) && queryRef.current.current === queryRef.current.previous) {
      isFetching.current = false
      setLoading(false) // Устанавливаем loading в false, если больше нечего загружать
      return
    }

    setLoading(true) // Устанавливаем loading в true только если начинается новая загрузка
    isFetching.current = true

    if (fetchPage) {
      defaultGetFunc()
    } else {
      fetchWithClearRedux()
    }
  }, [nextPage, requestFunction, apiHelper, updateAction, query, isListEmpty])

  const handleScroll = ({ target }) => {
    if (nextPage && !loading) {
      if ((target.scrollHeight - target.scrollTop - target.clientHeight) / target.scrollHeight <= 0.2) {
        setNextPage(currentPage.current + 1)
        fetchPage ? setFetchPage(fetchPage + 1) : setFetchPage(1)
      }
    }
  }

  const handleListDelete = () => {
    setIsListEmpty(true)
  }

  return { loading, nextPage, handleScroll, dataCount, setDataCount, handleListDelete }
}
